import type { Node, Edge } from '@xyflow/react';
import type { MockUiEvalCase, MockUiStateGraph } from '@erp/rpc';

/** Data carried by a `pathNode` — one *state* of the built mock. */
export interface PathNodeData extends Record<string, unknown> {
  /** The action that produced this state (the edge into it); `Start` for the root. */
  label: string;
  /** Screenshot of `?case&step` at this state, from the first case that captured it. */
  screenshot?: string;
  isRoot: boolean;
  /** Case ids that terminate at this node (rendered as badges). */
  caseIds: string[];
  /** The full action path from the root to this node (for the detail strip). */
  fullPath: string[];
  /** Task + expected end-state of the first case terminating here (detail strip). */
  task?: string;
  expect?: Record<string, unknown>;
  /** Captured `window.__evalState` at this state (from the persisted graph's node capture). */
  evalStateSnapshot?: Record<string, unknown>;
  /** Screen/view label this state belongs to. */
  screen?: string;
  /** Where this state was captured — `?case=<case>&step=<step>` (step -1 = initial, before any
   * action). Lets the detail overlay pair the built screenshot with its source storyboard frame.
   * Absent for trie-derived nodes (no anchors). */
  anchor?: { case: string; step: number };
  /** A leaf state — no outgoing action (a path ends here). */
  isTerminal?: boolean;
  /** SOP-projection overlay (AI-340): the id of the SOP taught for the path terminating here — the
   * node's executable walk. Set only on a taught path's terminal node. Deep-links to the SOP. */
  sopId?: string;
  /** SOP-projection overlay (AI-340): the golden `window.__evalState` of that SOP — the
   * deterministic eval's assertion for this state. */
  golden?: Record<string, unknown> | null;
}

export type PathNode = Node<PathNodeData, 'pathNode'>;

// Kept in sync with the CSS box in MockUiPathTree's `pathNode` so ELK spaces before React Flow
// measures the real DOM.
const NODE_WIDTH = 184;
const NODE_HEIGHT = 150;

const norm = (s: string): string => s.trim().replace(/\s+/g, ' ');

/**
 * Merge the eval cases on their shared action prefix into a tree for React Flow: nodes are states
 * (a screenshot of `?case=<id>&step=<n>`), edges are the actions between them. Cases that share an
 * opening sequence share those nodes and branch where their actions first differ — the branching
 * the flat per-case list can't show.
 *
 * ponytail: nodes merge on the action *string*. Two cases with the same action label but a
 * different resulting state (e.g. a lookup that shows a grid in one case, a popup in another) share
 * one node showing one case's shot; the branch still appears at the next step where labels diverge.
 * Upgrade to merge-by-`expect`/screenshot-hash only if that ever misleads.
 */
export function buildPathTree(cases: MockUiEvalCase[]): {
  nodes: PathNode[];
  edges: Edge[];
} {
  const nodes: PathNode[] = [];
  const edges: Edge[] = [];
  const byKey = new Map<string, PathNode>(); // prefix (JSON of normalized labels) -> node
  let nodeId = 0;
  let edgeId = 0;

  const makeNode = (data: PathNodeData): PathNode => {
    const node: PathNode = {
      id: `n${nodeId++}`,
      type: 'pathNode',
      position: { x: 0, y: 0 },
      measured: { width: NODE_WIDTH, height: NODE_HEIGHT },
      data,
    };
    nodes.push(node);
    return node;
  };

  const root = makeNode({
    label: 'Start',
    isRoot: true,
    caseIds: [],
    fullPath: [],
  });
  byKey.set('[]', root);

  for (const evalCase of cases) {
    let parent = root;
    const acc: string[] = [];
    (evalCase.path ?? []).forEach((raw, i) => {
      const label = norm(raw);
      if (!label) return;
      acc.push(label);
      const key = JSON.stringify(acc); // collision-safe prefix key
      let node = byKey.get(key);
      if (!node) {
        node = makeNode({
          label,
          screenshot: evalCase.shots[i] || undefined,
          isRoot: false,
          caseIds: [],
          fullPath: [...acc],
        });
        byKey.set(key, node);
        edges.push({
          id: `e${edgeId++}`,
          source: parent.id,
          target: node.id,
          label,
        });
      } else if (!node.data.screenshot && evalCase.shots[i]) {
        node.data.screenshot = evalCase.shots[i];
      }
      parent = node;
    });
    if (parent !== root && evalCase.id) {
      parent.data.caseIds.push(evalCase.id);
      if (parent.data.task === undefined) {
        parent.data.task = evalCase.task;
        parent.data.expect = evalCase.expect;
      }
    }
  }

  // A state with no outgoing action is a leaf — mark it terminal (the root never is).
  const sources = new Set(edges.map((e) => e.source));
  for (const node of nodes) {
    node.data.isTerminal = !node.data.isRoot && !sources.has(node.id);
  }

  return { nodes, edges };
}

/**
 * Map the PERSISTED state graph straight to React Flow — it is already node/edge-shaped (stable
 * ids, `source`/`target`, action labels, per-node screenshot + `__evalState`), and unlike the
 * cases trie it can *reconverge* (a real DAG). Preferred whenever the build emitted a
 * `state_graph`; `buildPathTree(cases)` is the fallback for legacy/in-flight builds.
 */
export function graphToFlow(graph: MockUiStateGraph): {
  nodes: PathNode[];
  edges: Edge[];
} {
  // paths that END at each node → the badges (which scenarios terminate in this state) + the SOP
  // taught for that path (SOP-projection overlay, AI-340). A node can terminate several paths;
  // the first path's sopId wins for the deep-link (one walk per node in practice).
  const terminalCaseIds = new Map<string, string[]>();
  const sopByTerminalNode = new Map<string, string>();
  for (const path of graph.paths) {
    const last = path.nodeIds[path.nodeIds.length - 1];
    if (!last) continue;
    let list = terminalCaseIds.get(last);
    if (!list) {
      list = [];
      terminalCaseIds.set(last, list);
    }
    list.push(path.id);
    if (path.sopId && !sopByTerminalNode.has(last)) {
      sopByTerminalNode.set(last, path.sopId);
    }
  }

  const nodes: PathNode[] = graph.nodes.map((n) => ({
    id: n.id,
    type: 'pathNode',
    position: { x: 0, y: 0 },
    measured: { width: NODE_WIDTH, height: NODE_HEIGHT },
    data: {
      label: n.label || n.id,
      screenshot: n.screenshot || undefined,
      isRoot: n.isRoot,
      caseIds: terminalCaseIds.get(n.id) ?? [],
      fullPath: [],
      expect: n.expect,
      evalStateSnapshot: n.evalStateSnapshot,
      screen: n.screen || undefined,
      anchor: n.anchor,
      isTerminal: n.isTerminal,
      sopId: sopByTerminalNode.get(n.id),
      golden: n.golden,
    },
  }));

  const edges: Edge[] = graph.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.action,
  }));

  return { nodes, edges };
}

/** One taught SOP for a `?case=`, as used by the SOP-projection overlay. */
export interface TaughtSopBinding {
  sopId: string;
  golden: Record<string, unknown> | null;
}

/**
 * The SOP projection (AI-340): overlay a mock's taught SOPs onto its state graph so the picture
 * becomes SOP-backed. For each path whose `?case=` has a taught SOP, set `path.sopId` (the path's
 * executable walk) and, on the path's TERMINAL node, `node.golden` (the SOP-version golden
 * `window.__evalState` — the deterministic eval's assertion). Pure; returns a new graph. The graph
 * never re-encodes the executable actions — the SOP steps stay the single source of truth.
 */
export function applySopProjection(
  graph: MockUiStateGraph,
  taughtByCase: ReadonlyMap<string, TaughtSopBinding>,
): MockUiStateGraph {
  if (taughtByCase.size === 0) return graph;
  const goldenByNode = new Map<string, Record<string, unknown> | null>();
  const paths = graph.paths.map((path) => {
    const taught = taughtByCase.get(path.id);
    if (!taught) return path;
    const terminal = path.nodeIds[path.nodeIds.length - 1];
    if (terminal && taught.golden) goldenByNode.set(terminal, taught.golden);
    return { ...path, sopId: taught.sopId };
  });
  const nodes = graph.nodes.map((node) =>
    goldenByNode.has(node.id)
      ? { ...node, golden: goldenByNode.get(node.id) ?? null }
      : node,
  );
  return { ...graph, paths, nodes };
}
